import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Wand2, Image as ImageIcon, X, Link as LinkIcon, Copy,
  Bold, Italic, Underline as UnderlineIcon,
  AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { sanitizeHtml, ALLOWED_TAG_REGEX } from "@/lib/sanitizeHtml";

/**
 * Iter 76 — this file NO LONGER renders a permanent right-side panel.
 * It is a library of reusable editor components (text / image / button /
 * style / advanced) consumed by the contextual floating toolbar
 * (ContextualToolbar.jsx) that appears next to the selected element while
 * Edit mode is active.
 */

export function typeLabel(type) {
  return {
    text: "Text block",
    image: "Image block",
    button: "Button",
    products: "Product collection",
    menu: "Menu",
    services: "Services list",
    gallery: "Gallery",
    header: "Site header",
    footer: "Site footer",
    testimonials: "Testimonials",
    faqs: "FAQ",
    pricing: "Pricing",
    team: "Team",
    trust_points: "Trust points",
    why_choose_us: "Why choose us",
    process_steps: "Process steps",
    announcement_messages: "Announcement messages",
  }[type] || "Element";
}

/* -------------------------------------------------------------------------- */
/* Text editor                                                                */
/* -------------------------------------------------------------------------- */

export function TextEditor({ selection, draft, publicPayload, updateField }) {
  const anchor = selection.anchor;

  // Iter-75 — resolve the CURRENT value for this text anchor. Priority order:
  //   1. User's uncommitted draft edit (this session).
  //   2. Persisted `website_content` value:
  //      - Scalar field (anchor.path) OR
  //      - Copy-override map (anchor.copyPath).
  //   3. Derived template value at `anchor.configPath` — the copy-tree the
  //      SRC template renders from.
  //   4. Empty string.
  const isOverride = !!anchor.copyPath;
  const wc = publicPayload.website_content || {};
  const draftValue = isOverride
    ? draft.copy_overrides?.[anchor.copyPath]
    : draft[anchor.path];
  const savedValue = isOverride
    ? wc.copy_overrides?.[anchor.copyPath]
    : wc[anchor.path];
  const derivedValue = anchor.configPath
    ? anchor.configPath.split(".").reduce(
        (obj, key) => (obj && typeof obj === "object" ? obj[key] : undefined),
        publicPayload,
      )
    : undefined;
  const current = draftValue ?? savedValue ?? derivedValue ?? "";
  const savePathKey = isOverride ? `copy_overrides.${anchor.copyPath}` : anchor.path;
  const testidKey = isOverride ? anchor.copyPath.replace(/\./g, "-") : anchor.path;

  const editorRef = useRef(null);
  const initialSetRef = useRef(false);
  const isHtml = typeof current === "string" && current.includes("<");

  // Mount contentEditable with initial value once per anchor
  useEffect(() => {
    initialSetRef.current = false;
  }, [savePathKey]);
  useEffect(() => {
    const el = editorRef.current;
    if (!el || initialSetRef.current) return;
    initialSetRef.current = true;
    if (isHtml) {
      el.innerHTML = sanitizeHtml(current || "");
    } else {
      el.innerText = current || "";
    }
  });

  const commit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML.trim();
    const plain = el.innerText.trim();
    const looksHtml = ALLOWED_TAG_REGEX.test(html);
    updateField(savePathKey, looksHtml ? sanitizeHtml(html) : plain);
  };

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    // Fire commit after execCommand so the draft picks up the new HTML
    setTimeout(commit, 0);
  };

  const addLink = () => {
    const href = window.prompt("Link URL (leave empty to remove):", "");
    if (href === null) return;
    if (href === "") { exec("unlink"); return; }
    const finalHref = /^(https?:|mailto:|tel:|\/|#)/i.test(href) ? href : `https://${href}`;
    exec("createLink", finalHref);
  };

  return (
    <div className="space-y-3">
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600">
        {anchor.label}
      </label>

      {/* Formatting toolbar */}
      <div className="flex items-center gap-0.5 p-1 rounded-md bg-gray-100 border border-gray-200" data-testid="text-editor-toolbar">
        <FmtBtn testId="text-fmt-bold" title="Bold" onClick={() => exec("bold")} icon={Bold} />
        <FmtBtn testId="text-fmt-italic" title="Italic" onClick={() => exec("italic")} icon={Italic} />
        <FmtBtn testId="text-fmt-underline" title="Underline" onClick={() => exec("underline")} icon={UnderlineIcon} />
        <FmtBtn testId="text-fmt-link" title="Link" onClick={addLink} icon={LinkIcon} />
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <FmtBtn testId="text-fmt-clear" title="Clear formatting" onClick={() => exec("removeFormat")} icon={X} />
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onBlur={commit}
        onInput={commit}
        onMouseDown={(e) => e.stopPropagation()}
        className="min-h-[72px] p-2 rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-base whitespace-pre-wrap break-words"
        data-testid={`inline-editor-text-input-${testidKey}`}
        style={{ maxWidth: "100%" }}
      />
      <p className="text-[11px] text-gray-500 leading-relaxed">
        Tip: press <b>Ctrl/Cmd + S</b> to save. Formatting round-trips as HTML;
        headings and body text render the formatting on the live site.
      </p>
    </div>
  );
}

function FmtBtn({ icon: Icon, title, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      title={title}
      data-testid={testId}
      className="w-7 h-7 rounded flex items-center justify-center hover:bg-white hover:shadow-sm"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Image editor                                                               */
/* -------------------------------------------------------------------------- */

const ASPECT_RATIOS = [
  { value: "square",    label: "Square",    ratio: "1 / 1" },
  { value: "portrait",  label: "Portrait",  ratio: "3 / 4" },
  { value: "landscape", label: "Landscape", ratio: "4 / 3" },
  { value: "widescreen", label: "16:9 Banner", ratio: "16 / 9" },
  { value: "banner",    label: "Full-width", ratio: "21 / 9" },
];

export function ImageEditor({ selection, draft, publicPayload, updateField, bizId }) {
  const anchor = selection.anchor;
  const deref = (p) => (p ? p.split(".").reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), publicPayload) : undefined);
  const derived = deref(anchor.configPath) ?? (anchor.configPaths || []).map(deref).find((v) => v != null);
  const current = draft[anchor.path] ?? publicPayload.website_content?.[anchor.path] ?? derived ?? "";
  const altKey = `${anchor.path}_alt`;
  const captionKey = `${anchor.path}_caption`;

  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/business/${bizId}/media/regenerate`, { slot: "hero" });
      if (data?.image_url) {
        updateField(anchor.path, data.image_url);
        toast.success("Generated a new image.");
      } else {
        toast.error("Image generation returned no URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't regenerate image");
    } finally {
      setRegenerating(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.image_url) {
        updateField(anchor.path, data.image_url);
        toast.success("Image uploaded.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview */}
      {current ? (
        <img
          src={current}
          alt=""
          className="w-full aspect-video object-cover rounded-md border border-gray-200"
          data-testid="inline-editor-image-preview"
        />
      ) : (
        <div className="w-full aspect-video rounded-md border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
          <ImageIcon className="w-6 h-6" />
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-3 gap-2">
        <ActionBtn onClick={() => fileInputRef.current?.click()} icon={ImageIcon} label="Replace" testId="inline-editor-image-replace" busy={uploading} />
        <ActionBtn onClick={() => fileInputRef.current?.click()} icon={ImageIcon} label="Upload"  testId="inline-editor-image-upload" busy={uploading} />
        <ActionBtn onClick={regenerate} icon={Wand2} label="AI Generate" testId="inline-editor-image-ai-generate" busy={regenerating} />
      </div>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
      />

      {/* Image URL */}
      <FieldRow label="Image URL">
        <Input
          value={current}
          onChange={(e) => updateField(anchor.path, e.target.value)}
          placeholder="https://…"
          data-testid={`inline-editor-image-url-${anchor.path}`}
        />
      </FieldRow>

      {/* Alt text (accessibility/SEO) */}
      <FieldRow label="Alt text (accessibility & SEO)">
        <Input
          value={draft[altKey] ?? publicPayload.website_content?.[altKey] ?? ""}
          onChange={(e) => updateField(altKey, e.target.value)}
          placeholder="e.g. Modern kitchen with warm lighting"
          data-testid={`inline-editor-image-alt-${anchor.path}`}
        />
      </FieldRow>

      {/* Optional public caption */}
      <FieldRow label="Caption (optional — shown publicly)">
        <Input
          value={draft[captionKey] ?? publicPayload.website_content?.[captionKey] ?? ""}
          onChange={(e) => updateField(captionKey, e.target.value)}
          placeholder="A short caption if you want one to appear"
          data-testid={`inline-editor-image-caption-${anchor.path}`}
        />
        <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
          The AI prompt is private and never shown publicly. Only this caption
          field appears on the live site.
        </p>
      </FieldRow>

      {/* Aspect ratio */}
      <FieldRow label="Aspect ratio">
        <div className="grid grid-cols-5 gap-1">
          {ASPECT_RATIOS.map((r) => (
            <button
              key={r.value}
              onClick={() => updateField(`${anchor.path}_aspect`, r.value)}
              className={`px-2 py-1.5 rounded-md border text-[10px] font-bold ${(draft[`${anchor.path}_aspect`] ?? publicPayload.website_content?.[`${anchor.path}_aspect`]) === r.value ? "bg-emerald-500 text-white border-emerald-600" : "border-gray-200 hover:bg-gray-50"}`}
              title={r.label}
              data-testid={`inline-editor-image-aspect-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </FieldRow>

      {/* Focal point (X / Y percentages) */}
      <FieldRow label="Focal point">
        <FocalPointPicker
          value={draft[`${anchor.path}_focal`] ?? publicPayload.website_content?.[`${anchor.path}_focal`] ?? { x: 50, y: 50 }}
          onChange={(v) => updateField(`${anchor.path}_focal`, v)}
          preview={current}
        />
      </FieldRow>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, busy, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="flex flex-col items-center justify-center gap-1 py-3 rounded-lg border border-gray-200 hover:border-black hover:bg-gray-50 disabled:opacity-50"
      data-testid={testId}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      <span className="text-[11px] font-bold">{label}</span>
    </button>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/**
 * Simple focal-point picker: click anywhere inside the preview to set X/Y
 * percentages. A visible crosshair marks the current focal point.
 */
function FocalPointPicker({ value, onChange, preview }) {
  const boxRef = useRef(null);
  const onBoxClick = (e) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100);
    const y = Math.round(((e.clientY - r.top) / r.height) * 100);
    onChange({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };
  return (
    <div>
      <div
        ref={boxRef}
        onClick={onBoxClick}
        className="relative w-full aspect-video rounded-md border border-gray-300 overflow-hidden cursor-crosshair bg-gray-100"
        data-testid="inline-editor-focal-picker"
      >
        {preview && (
          <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        )}
        <div
          className="absolute w-4 h-4 -mt-2 -ml-2 rounded-full bg-emerald-500 border-2 border-white shadow-lg pointer-events-none"
          style={{ top: `${value.y}%`, left: `${value.x}%` }}
        />
      </div>
      <div className="mt-1 text-[11px] text-gray-500">
        {value.x}% × {value.y}%
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Button editor                                                              */
/* -------------------------------------------------------------------------- */

export function ButtonEditor({ selection, draft, publicPayload, updateField }) {
  const anchor = selection.anchor;
  const wc = publicPayload.website_content || {};
  const ov = { ...(wc.copy_overrides || {}), ...(draft.copy_overrides || {}) };
  const deref = (p) => (p ? p.split(".").reduce((o, k) => (o && typeof o === "object" ? o[k] : undefined), publicPayload) : undefined);

  // Iter 77 — buttons whose copy lives in `copy_overrides` (template
  // defaults are hardcoded) instead of scalar website_content fields.
  const labelIsOverride = !!anchor.copyPath;
  const linkIsOverride = !!anchor.linkCopyPath;
  const label = labelIsOverride
    ? (ov[anchor.copyPath] ?? deref(anchor.configPath) ?? anchor.defaultLabel ?? "")
    : (draft[anchor.path] ?? wc[anchor.path] ?? anchor.defaultLabel ?? "");
  const link = linkIsOverride
    ? (ov[anchor.linkCopyPath] ?? deref(anchor.linkConfigPath) ?? anchor.defaultLink ?? "")
    : (draft[anchor.linkPath] ?? wc[anchor.linkPath] ?? anchor.defaultLink ?? "");
  const labelKey = labelIsOverride ? `copy_overrides.${anchor.copyPath}` : anchor.path;
  const linkKey = linkIsOverride ? `copy_overrides.${anchor.linkCopyPath}` : anchor.linkPath;
  const textTestId = labelIsOverride ? anchor.copyPath.replace(/\./g, "-") : anchor.path;
  const newTabKey = anchor.path ? `${anchor.path}_newtab` : null;

  return (
    <div className="space-y-3">
      <FieldRow label="Button text">
        <Input
          value={label}
          onChange={(e) => updateField(labelKey, e.target.value)}
          placeholder="Book a consultation"
          data-testid={`inline-editor-button-text-${textTestId}`}
        />
      </FieldRow>
      {!anchor.noLink && (linkKey ? (
        <FieldRow label="Button link">
          <div className="relative">
            <LinkIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={link}
              onChange={(e) => updateField(linkKey, e.target.value)}
              placeholder="#contact or https://..."
              className="pl-8"
              data-testid={`inline-editor-button-link-${linkIsOverride ? anchor.linkCopyPath.replace(/\./g, "-") : anchor.linkPath}`}
            />
          </div>
        </FieldRow>
      ) : null)}
      {anchor.noLink && (
        <p className="text-[11px] text-gray-500 leading-relaxed">
          This button scrolls to a section of the page — its action is part of
          the template, but the text is yours.
        </p>
      )}
      {newTabKey && !anchor.noLink && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`newtab-${anchor.path}`}
            checked={!!(draft[newTabKey] ?? wc[newTabKey])}
            onChange={(e) => updateField(newTabKey, e.target.checked)}
            data-testid={`inline-editor-button-newtab-${anchor.path}`}
          />
          <label htmlFor={`newtab-${anchor.path}`} className="text-xs text-gray-700">
            Open in new tab
          </label>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Phase 3B — Style + Advanced tabs                                           */
/* -------------------------------------------------------------------------- */

export function StyleTab({ selection, draft, publicPayload, updateField }) {
  const anchor = selection.anchor;
  const anchorKey = anchor.path || anchor.testid;
  const wc = publicPayload?.website_content || {};
  const styleMap = draft.element_styles ?? wc.element_styles ?? {};
  const current = styleMap[anchorKey] || {};
  const set = (patch) => {
    const nextEntry = { ...current, ...patch };
    // Strip empty
    Object.keys(nextEntry).forEach((k) => (nextEntry[k] === "" || nextEntry[k] === null) && delete nextEntry[k]);
    const nextMap = { ...styleMap };
    if (Object.keys(nextEntry).length === 0) delete nextMap[anchorKey];
    else nextMap[anchorKey] = nextEntry;
    updateField("element_styles", nextMap);
  };

  const paddingOptions = [
    { value: "sm", label: "S" },
    { value: "md", label: "M" },
    { value: "lg", label: "L" },
  ];
  const alignOptions = [
    { value: "left",   Icon: AlignLeft },
    { value: "center", Icon: AlignCenter },
    { value: "right",  Icon: AlignRight },
  ];

  return (
    <div className="space-y-4" data-testid="style-tab">
      <p className="text-xs text-gray-600 leading-relaxed">
        Style overrides for <b>{anchor.label}</b>. Empty values inherit the
        template&apos;s default styling.
      </p>

      <StyleField label="Background color">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={current.background_color || "#ffffff"}
            onChange={(e) => set({ background_color: e.target.value })}
            className="w-10 h-9 rounded border border-gray-200"
            data-testid="style-tab-bg-swatch"
          />
          <Input
            value={current.background_color || ""}
            onChange={(e) => set({ background_color: e.target.value })}
            placeholder="#ffffff"
            className="flex-1 font-mono text-xs"
            data-testid="style-tab-bg-input"
          />
          {current.background_color && (
            <button onClick={() => set({ background_color: "" })} className="text-[11px] text-gray-500 hover:text-black underline" data-testid="style-tab-bg-clear">
              Clear
            </button>
          )}
        </div>
      </StyleField>

      <StyleField label="Text color">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={current.text_color || "#111111"}
            onChange={(e) => set({ text_color: e.target.value })}
            className="w-10 h-9 rounded border border-gray-200"
            data-testid="style-tab-text-swatch"
          />
          <Input
            value={current.text_color || ""}
            onChange={(e) => set({ text_color: e.target.value })}
            placeholder="#111111"
            className="flex-1 font-mono text-xs"
            data-testid="style-tab-text-input"
          />
          {current.text_color && (
            <button onClick={() => set({ text_color: "" })} className="text-[11px] text-gray-500 hover:text-black underline" data-testid="style-tab-text-clear">
              Clear
            </button>
          )}
        </div>
      </StyleField>

      <StyleField label="Text alignment">
        <div className="flex gap-1">
          {alignOptions.map((a) => (
            <button
              key={a.value}
              onClick={() => set({ text_align: a.value })}
              className={`flex-1 h-9 rounded-md border ${current.text_align === a.value ? "bg-emerald-500 text-white border-emerald-600" : "border-gray-200 hover:border-black"}`}
              data-testid={`style-tab-align-${a.value}`}
            >
              <a.Icon className="w-3.5 h-3.5 mx-auto" />
            </button>
          ))}
        </div>
      </StyleField>

      <StyleField label="Padding">
        <div className="flex gap-1">
          {paddingOptions.map((p) => (
            <button
              key={p.value}
              onClick={() => set({ padding: p.value })}
              className={`flex-1 h-9 rounded-md border text-xs font-bold ${current.padding === p.value ? "bg-emerald-500 text-white border-emerald-600" : "border-gray-200 hover:border-black"}`}
              data-testid={`style-tab-padding-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </StyleField>
    </div>
  );
}

export function AdvancedTab({ selection, draft, publicPayload, updateField }) {
  const anchor = selection.anchor;
  const anchorKey = anchor.path || anchor.testid;
  const wc = publicPayload?.website_content || {};
  const advMap = draft.element_advanced ?? wc.element_advanced ?? {};
  const current = advMap[anchorKey] || {};
  const set = (patch) => {
    const nextEntry = { ...current, ...patch };
    Object.keys(nextEntry).forEach((k) => (nextEntry[k] === "" || nextEntry[k] === null) && delete nextEntry[k]);
    const nextMap = { ...advMap };
    if (Object.keys(nextEntry).length === 0) delete nextMap[anchorKey];
    else nextMap[anchorKey] = nextEntry;
    updateField("element_advanced", nextMap);
  };

  return (
    <div className="space-y-4" data-testid="advanced-tab">
      <p className="text-xs text-gray-600 leading-relaxed">
        Advanced attributes for <b>{anchor.label}</b>. These affect the
        rendered HTML and are helpful for custom CSS, deep linking, or
        analytics tracking.
      </p>
      <StyleField label="Custom CSS class">
        <Input
          value={current.css_class || ""}
          onChange={(e) => set({ css_class: e.target.value })}
          placeholder="my-custom-class"
          data-testid="advanced-tab-css-class"
        />
        <p className="text-[11px] text-gray-500 mt-1">Space-separated Tailwind or custom classes.</p>
      </StyleField>
      <StyleField label="Test ID">
        <Input
          value={anchor.testid}
          readOnly
          className="text-xs bg-gray-50"
          data-testid="advanced-tab-testid"
        />
        <p className="text-[11px] text-gray-500 mt-1">Read-only. Provided by the template.</p>
      </StyleField>
      <StyleField label="Anchor ID">
        <Input
          value={current.anchor_id || ""}
          onChange={(e) => set({ anchor_id: e.target.value })}
          placeholder="my-section"
          data-testid="advanced-tab-anchor-id"
        />
        <p className="text-[11px] text-gray-500 mt-1">Use for deep-linking (e.g. #my-section).</p>
      </StyleField>
    </div>
  );
}

function StyleField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
